import { useLocale } from "@/i18n/LocaleProvider";
import { LocalizedText } from "@/i18n/LocalizedText";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";
import { capturePosthogEvent } from "@/lib/posthog";
import {
  billingAPI,
  configAPI,
  authAPI,
  type BillingInterval,
  type BillingPlan,
  type Plan,
} from "@/services/api";
import { useQuery } from "@tanstack/react-query";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  ArrowLeft,
  Bot,
  BriefcaseBusiness,
  CheckCircle2,
  CircleDashed,
  Download,
  HandHelping,
  Info,
  LogOut,
  MapPin,
  SquareTerminal,
  Users,
  type LucideIcon,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import noboriLogo from "../assets/logo.svg";
import { signOut } from "firebase/auth";
import { auth } from "../firebase";
import { SeeYourVisibility } from "./SeeYourFullVisibility";
import { useAuth } from "@/components/AuthContext";

type OnboardingPlanKey = BillingPlan | "enterprise";
const AI_PLATFORM_FEATURE_INDEX = 3;

const FEATURE_ROWS: Array<{ label: string; icon: LucideIcon; info: string }> = [
  {
    label: "Prompts",
    icon: SquareTerminal,
    info: "Total number of prompts you can track across all brands.",
  },
  {
    label: "Brands",
    icon: BriefcaseBusiness,
    info: "How many brands or projects you can monitor.",
  },
  {
    label: "Country",
    icon: MapPin,
    info: "Number of countries you can run prompts from.",
  },
  {
    label: "AI Platforms",
    icon: Bot,
    info: "AI platforms used to run your prompts.",
  },
  {
    label: "Runs / month",
    icon: CircleDashed,
    info: "Total prompt executions per month across all platforms.",
  },
  {
    label: "Seats",
    icon: Users,
    info: "Number of team members with access.",
  },
  {
    label: "Reports & Exports",
    icon: Download,
    info: "Download data format for analysis or sharing.",
  },
  {
    label: "Support",
    icon: HandHelping,
    info: "Available support channels for this plan.",
  },
];

const FALLBACK_PRICES = {
  starter: { monthly: 59, yearly: 41 },
  growth: { monthly: 149, yearly: 125 },
};

const FALLBACK_PLAN_CONTENT: Record<
  OnboardingPlanKey,
  { name: string; description: string; features: string[] }
> = {
  starter: {
    name: "Starter",
    description: "Perfect for individual users",
    features: [
      "25 prompts tracked",
      "1 brand",
      "1 country",
      "ChatGPT",
      "1 000",
      "1 seat",
      "CSV",
      "Standard email & in-app chat",
    ],
  },
  growth: {
    name: "Growth",
    description: "Ideal for professionals and small teams",
    features: [
      "100 prompts tracked",
      "5 brands",
      "5 countries",
      "ChatGPT, Google AI Overviews, Perplexity",
      "9 000",
      "Unlimited",
      "CSV",
      "Standard email & in-app chat",
    ],
  },
  enterprise: {
    name: "Enterprise",
    description: "Best for large teams and enterprise-level organizations",
    features: Array(FEATURE_ROWS.length).fill("Custom"),
  },
};

const parseMoney = (value: unknown): number | null => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const parseLimitNumber = (value: unknown): number | null => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const formatCount = (
  value: number | null,
  singular: string,
  plural: string,
) => {
  if (value === null) return null;
  return `${value.toLocaleString("en-US").replace(/,/g, " ")} ${value === 1 ? singular : plural}`;
};

const normalizeYearlyToMonthly = (
  yearlyRaw: number | null,
  monthlyRaw: number | null,
  fallback: number,
) => {
  if (yearlyRaw === null) return fallback;
  if (monthlyRaw !== null && yearlyRaw > monthlyRaw) {
    return Math.round(yearlyRaw / 12);
  }
  return Math.round(yearlyRaw);
};

const toPlanKey = (name: string): OnboardingPlanKey | null => {
  const normalized = name.trim().toLowerCase();
  if (normalized === "starter") return "starter";
  if (normalized === "growth") return "growth";
  if (normalized === "enterprise") return "enterprise";
  return null;
};

const buildAiPlatformsText = (plan: Plan) => {
  const overviewsEnabled = Boolean(plan.limits["ai.overviews.enabled"]);
  const aiModeEnabled = Boolean(
    plan.limits["ai.mode.enabled"] ?? plan.limits["ai.aimode.enabled"],
  );
  const parts = [
    plan.limits["ai.chatgpt.enabled"] ? "ChatGPT" : null,
    overviewsEnabled ? "Google AI Overview" : null,
    aiModeEnabled ? "AI mode" : null,
    plan.limits["ai.gemini.enabled"] ? "Gemini" : null,
    plan.limits["ai.perplexity.enabled"] ? "Perplexity" : null,
    plan.limits["ai.grok.enabled"] ? "Grok" : null,
    plan.limits["ai.copilot.enabled"] ? "Copilot" : null,
  ].filter(Boolean) as string[];
  return parts.join(", ");
};

const derivePlanFeatures = (
  plan: Plan | undefined,
  fallbackFeatures: string[],
) => {
  if (!plan) return fallbackFeatures;

  const promptsTracked = formatCount(
    parseLimitNumber(plan.limits["prompts.max_tracked"]),
    "prompt tracked",
    "prompts tracked",
  );
  const brands = formatCount(
    parseLimitNumber(plan.limits["brands.max"]),
    "brand",
    "brands",
  );
  const countries = formatCount(
    parseLimitNumber(plan.limits["countries.max"]),
    "country",
    "countries",
  );
  const runs = parseLimitNumber(plan.limits["runs.monthly"]);
  const seatsLimit =
    parseLimitNumber((plan.limits as Record<string, unknown>)["seats.max"]) ??
    parseLimitNumber((plan.limits as Record<string, unknown>)["users.max"]);
  const seatsText =
    seatsLimit === null
      ? "Unlimited"
      : `${seatsLimit.toLocaleString("en-US").replace(/,/g, " ")} ${seatsLimit === 1 ? "seat" : "seats"}`;
  const aiPlatforms = buildAiPlatformsText(plan);
  const csvEnabled = Boolean(plan.limits["exports.csv.enabled"]);
  const custom = Boolean(plan.custom);
  const customSeats = String(
    (plan.customText as Record<string, unknown> | undefined)?.seats || "",
  ).trim();

  const derived = [
    custom ? plan.customText?.prompts : promptsTracked,
    custom ? plan.customText?.brands : brands,
    custom ? plan.customText?.countries : countries,
    aiPlatforms,
    custom
      ? plan.customText?.runs
      : runs === null
        ? null
        : runs.toLocaleString("en-US").replace(/,/g, " "),
    custom ? customSeats || "Unlimited" : seatsText,
    custom ? plan.customText?.export : csvEnabled ? "CSV" : "Custom",
    custom ? plan.customText?.support : "Standard email & in-app chat",
  ];

  return derived.map((value, index) => {
    const normalized = String(value || "").trim();
    if (normalized.length > 0) return normalized;
    return fallbackFeatures[index] || "Custom";
  });
};

const resolveOnboardingPlans = (plans: Plan[] | undefined) => {
  const mapped = new Map<OnboardingPlanKey, Plan>();
  const activePlans = (plans || []).filter((plan) => plan?.active !== false);
  for (const plan of activePlans) {
    const key = toPlanKey(String(plan?.name || ""));
    if (key) mapped.set(key, plan);
  }

  return {
    starter: mapped.get("starter"),
    growth: mapped.get("growth"),
    enterprise: mapped.get("enterprise"),
  };
};

const resolveOnboardingPrices = (
  planMap: ReturnType<typeof resolveOnboardingPlans>,
) => {
  const starterMonthly = parseMoney(planMap.starter?.monthlyPrice);
  const starterYearly = parseMoney(planMap.starter?.yearlyPrice);
  const growthMonthly = parseMoney(planMap.growth?.monthlyPrice);
  const growthYearly = parseMoney(planMap.growth?.yearlyPrice);

  return {
    starter: {
      monthly: starterMonthly ?? FALLBACK_PRICES.starter.monthly,
      yearly: normalizeYearlyToMonthly(
        starterYearly,
        starterMonthly,
        FALLBACK_PRICES.starter.yearly,
      ),
    },
    growth: {
      monthly: growthMonthly ?? FALLBACK_PRICES.growth.monthly,
      yearly: normalizeYearlyToMonthly(
        growthYearly,
        growthMonthly,
        FALLBACK_PRICES.growth.yearly,
      ),
    },
  };
};

const OnboardingPlanPage = () => {
  const { t } = useLocale();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [interval, setInterval] = useState<BillingInterval>("monthly");
  const [loadingCta, setLoadingCta] = useState<string | null>(null);
  const [isSeeYourVisibiliy, setSeeYourVisibility] = useState(true);
  const onboardingBrandId = useMemo(
    () =>
      String(
        (Array.isArray(user?.brands) && user.brands[0]?.brandId) ||
          (Array.isArray(user?.brands) && user.brands[0]?.id) ||
          "",
      ).trim(),
    [user?.brands],
  );

  const { data: plans = [], isLoading: plansLoading } = useQuery({
    queryKey: ["onboardingPlansConfig"],
    queryFn: () => configAPI.getPlans(),
    staleTime: 60_000,
  });

  const { data: subscription } = useQuery({
    queryKey: ["onboardingBillingSubscription"],
    queryFn: () => billingAPI.getSubscription(),
    staleTime: 10_000,
  });

  const planMap = useMemo(() => resolveOnboardingPlans(plans), [plans]);
  const prices = useMemo(() => resolveOnboardingPrices(planMap), [planMap]);

  const starterFeatures = useMemo(
    () =>
      derivePlanFeatures(
        planMap.starter,
        FALLBACK_PLAN_CONTENT.starter.features,
      ),
    [planMap.starter],
  );
  const growthFeatures = useMemo(
    () =>
      derivePlanFeatures(planMap.growth, FALLBACK_PLAN_CONTENT.growth.features),
    [planMap.growth],
  );
  const enterpriseFeatures = useMemo(
    () =>
      derivePlanFeatures(
        planMap.enterprise,
        FALLBACK_PLAN_CONTENT.enterprise.features,
      ),
    [planMap.enterprise],
  );
  const shouldExpandAiPlatformsRow = useMemo(() => {
    const aiPlatformValues = [
      starterFeatures[AI_PLATFORM_FEATURE_INDEX],
      growthFeatures[AI_PLATFORM_FEATURE_INDEX],
      enterpriseFeatures[AI_PLATFORM_FEATURE_INDEX],
    ].map((value) => String(value || "").trim());

    return aiPlatformValues.some(
      (value) => value.length > 40 || value.includes(","),
    );
  }, [starterFeatures, growthFeatures, enterpriseFeatures]);

  useEffect(() => {
    capturePosthogEvent("plan_page_viewed", { source: "onboarding" });
  }, []);

  useEffect(() => {
    if ((location.state as any)?.fromOnboarding) {
      window.sessionStorage.setItem("nobori_redirect_to_strategy_once", "1");
    }
  }, [location.state]);

  useEffect(() => {
    if (subscription?.can_enter_app) {
      const shouldRedirectToStrategy =
        window.sessionStorage.getItem("nobori_redirect_to_strategy_once") ===
        "1";
      if (shouldRedirectToStrategy) {
        window.sessionStorage.setItem(
          "nobori_onboarding_strategy_entry_once",
          "1",
        );
        window.sessionStorage.removeItem("nobori_redirect_to_strategy_once");
        navigate("/dashboard/get-cited/strategy", { replace: true });
        return;
      }
      navigate("/dashboard/overview", { replace: true });
    }
  }, [navigate, subscription?.can_enter_app]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const checkout = String(params.get("checkout") || "")
      .trim()
      .toLowerCase();
    const canceledPlan = String(params.get("plan") || "")
      .trim()
      .toLowerCase() as BillingPlan;
    const canceledInterval = String(params.get("interval") || "")
      .trim()
      .toLowerCase() as BillingInterval;
    if (checkout !== "cancel") return;
    if (!["starter", "growth"].includes(canceledPlan)) return;
    if (!["monthly", "yearly"].includes(canceledInterval)) return;

    const key = `nobori_stripe_cancel_v1:${canceledPlan}:${canceledInterval}:${location.search}`;
    if (window.sessionStorage.getItem(key) !== "1") {
      capturePosthogEvent("stripe_checkout_canceled", {
        plan: canceledPlan,
        interval: canceledInterval,
      });
      window.sessionStorage.setItem(key, "1");
    }

    navigate("/onboarding/plan", { replace: true });
  }, [location.search, navigate]);

  const handleCheckout = async (plan: BillingPlan) => {
    const actionKey = `${plan}:${interval}`;
    setLoadingCta(actionKey);
    try {
      const result = await billingAPI.checkout({ plan, interval });
      capturePosthogEvent("onboarding_plan_selected", {
        plan: result.plan,
        interval: result.interval,
        trial_eligible: result.trial_eligible,
      });
      capturePosthogEvent("stripe_checkout_started", {
        plan: result.plan,
        interval: result.interval,
        trial_applied: result.trial_applied,
        source: "onboarding",
      });
      window.open(result.checkout_url, "_self");
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message || "Unable to start checkout",
        {
          position: "bottom-right",
        },
      );
      setLoadingCta(null);
    }
  };

  const handleSignOut = async () => {
    try {
      await authAPI.logout().catch(() => undefined);
      await signOut(auth).catch(() => undefined);
      navigate("/login", { replace: true });
    } catch {
      toast.error(t("legacy.couldnt.log.out.1diajr7"), {
        position: "bottom-right",
      });
    }
  };

  const handleBackToVisibility = () => {
    capturePosthogEvent("onboarding_plan_back_clicked", {
      brand_id: onboardingBrandId || null,
    });
    setSeeYourVisibility(true);
  };

  const renderPrice = (plan: BillingPlan) => {
    const amount = prices[plan][interval];
    const monthly = prices[plan].monthly;
    return (
      <p className="flex h-[52px] items-end gap-1">
        {interval === "yearly" && (
          <span className="text-4xl font-light text-[#A3A3A3] line-through decoration-2">
            ${monthly}
          </span>
        )}
        <span className="text-4xl font-semibold leading-none">${amount}</span>
        <span className="text-muted-foreground">/mo</span>
      </p>
    );
  };

  const renderFeatureCell = (
    value: string,
    emphasized = false,
    key?: string | number,
    forceWrap = false,
  ) => {
    const isLongFeature = forceWrap || String(value || "").length > 56;
    return (
      <div
        key={key}
        className={cn(
          "flex min-w-0 flex-col justify-center border-b border-[#E5E5E5] text-sm last:border-b-0",
          isLongFeature ? "min-h-[84px] h-auto py-2" : "h-[52px]",
        )}
      >
        <div
          className={cn(
            "flex h-full min-w-0 gap-3",
            forceWrap ? "items-start" : "items-center",
          )}
        >
          <CheckCircle2 className="h-5 w-5 shrink-0 text-[#16A34A]" />
          <span
            className={cn(
              forceWrap
                ? "min-w-0 max-w-full whitespace-normal break-words leading-5"
                : "min-w-0 break-words leading-5",
              emphasized ? "font-semibold" : "text-[#262626]",
            )}
          >
            {value}
          </span>
        </div>
      </div>
    );
  };

  const growthLabel = String(
    planMap.growth?.metadata?.label || "Popular",
  ).trim();

  return (
    <>
      {isSeeYourVisibiliy ? (
        <SeeYourVisibility
          handleSignOut={handleSignOut}
          setSeeYourVisibility={setSeeYourVisibility}
          brandId={onboardingBrandId || undefined}
        />
      ) : (
        <div className="min-h-screen bg-[#F5F5F5]">
          <header className="h-16 border-b bg-white shadow-sm">
            <div className="mx-auto flex h-full w-full max-w-[1440px] items-center justify-between px-4 md:px-6">
              <div className="flex items-center gap-3">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 cursor-pointer text-[#525252] hover:bg-[#F5F5F5] hover:text-[#0A0A0A]"
                  onClick={handleBackToVisibility}
                  aria-label={t("legacy.back.to.visibility.snapshot.56hezj")}
                >
                  <ArrowLeft className="h-4 w-4" />
                </Button>
                <img
                  src={noboriLogo}
                  alt={t("legacy.nobori.ai.wa268k")}
                  className="h-7 w-auto"
                />
              </div>
              <Button
                variant="outline"
                size="sm"
                className="h-8 cursor-pointer border-[#E5E5E5] px-3 text-[#0A0A0A] shadow-xs"
                onClick={handleSignOut}
              >
                <LocalizedText id="legacy.sign.out.1jmaku0" />
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </header>

          <div className="mx-auto w-full max-w-[1440px] px-4 py-6 md:px-6">
            <header className="mb-6 text-center">
              <h1 className="text-xl font-semibold text-[#171717]">
                <LocalizedText id="legacy.choose.your.plan.1gd5zlm" />
              </h1>
              <p className="mt-2 text-sm text-[#737373]">
                <LocalizedText id="legacy.start.with.a.7.day.starter.trial.or.epju88" />
              </p>
            </header>

            <section>
              {plansLoading ? (
                <div className="flex min-h-[320px] items-center justify-center">
                  <Spinner variant="circle-filled" size={44} />
                </div>
              ) : (
                <div className="mx-auto grid w-full max-w-[1320px] overflow-hidden rounded-2xl border border-[#E5E5E5] bg-white pt-3 md:grid-cols-[300px_minmax(0,1fr)_minmax(0,1.12fr)_minmax(0,1fr)]">
                  <div className="min-h-[668px] h-auto border-r border-[#E5E5E5] bg-[#FAFAFA] pt-6 pb-2">
                    <div className="px-6">
                      <p className="mb-4 text-sm text-[#737373]">
                        <LocalizedText id="legacy.billed.16sw0ut" />
                      </p>
                      <div className="inline-flex h-[50px] w-full max-w-[255px] items-center rounded-xl border border-[#E5E5E5] bg-[#F5F5F5] p-1">
                        <button
                          type="button"
                          onClick={() => setInterval("monthly")}
                          className={cn(
                            "flex h-10 w-[75px] cursor-pointer items-center justify-center rounded-lg text-[14px] font-medium transition",
                            interval === "monthly"
                              ? "border border-[#E5E5E5] bg-white text-[#0A0A0A] shadow-[0_1px_3px_rgba(0,0,0,0.1)]"
                              : "text-[#737373] drop-shadow-[0_1px_3px_rgba(0,0,0,0.1)]",
                          )}
                        >
                          <LocalizedText id="legacy.monthly.hvlts0" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setInterval("yearly")}
                          className={cn(
                            "flex h-[42px] w-[172px] cursor-pointer items-center justify-center gap-2 rounded-lg px-2 text-[14px] font-medium transition",
                            interval === "yearly"
                              ? "border border-[#E5E5E5] bg-white text-[#0A0A0A] shadow-[0_1px_3px_rgba(0,0,0,0.1)]"
                              : "text-[#737373] drop-shadow-[0_1px_3px_rgba(0,0,0,0.1)]",
                          )}
                        >
                          <LocalizedText id="legacy.yearly.1kkgywv" />
                          <Badge
                            variant="outline"
                            className={cn(
                              "h-[22px] border-[#16A34A] bg-transparent px-[10px] py-[2px] text-[12px] font-medium text-[#16A34A]",
                              interval === "yearly"
                                ? "rounded-[6px]"
                                : "rounded-full",
                            )}
                          >
                            <LocalizedText id="legacy.2.months.free.1f8xg68" />
                          </Badge>
                        </button>
                      </div>
                    </div>

                    <div className="mt-24 px-6">
                      <p className="mb-2 text-sm text-[#737373]">
                        <LocalizedText id="legacy.key.features.6p1b0v" />
                      </p>
                      {FEATURE_ROWS.map((feature, index) => {
                        const Icon = feature.icon;
                        return (
                          <div
                            key={feature.label}
                            className={cn(
                              "flex items-center justify-between border-b border-[#E5E5E5] last:border-b-0",
                              index === AI_PLATFORM_FEATURE_INDEX &&
                                shouldExpandAiPlatformsRow
                                ? "min-h-[84px] h-auto py-2"
                                : "h-[52px]",
                            )}
                          >
                            <div className="flex min-w-0 items-center gap-3 text-sm text-[#404040]">
                              <Icon className="h-4 w-4 text-[#525252]" />
                              <span className="min-w-0 truncate">
                                {feature.label}
                              </span>
                            </div>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  type="button"
                                  className="cursor-pointer"
                                >
                                  <Info className="h-4 w-4 text-[#A3A3A3]" />
                                </button>
                              </TooltipTrigger>
                              <TooltipContent side="right">
                                {feature.info}
                              </TooltipContent>
                            </Tooltip>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className="min-h-[668px] h-auto min-w-0 px-6 pt-6 pb-2">
                    <h2 className="text-xl font-semibold leading-7">
                      {planMap.starter?.name ||
                        FALLBACK_PLAN_CONTENT.starter.name}
                    </h2>
                    <p className="mt-2 h-[40px] overflow-hidden text-sm text-[#737373]">
                      {planMap.starter?.description ||
                        FALLBACK_PLAN_CONTENT.starter.description}
                    </p>
                    <div className="mt-4">{renderPrice("starter")}</div>
                    <Button
                      className="mt-4 h-9 w-full cursor-pointer bg-[#FF5E00] text-white hover:bg-[#ff7a30] disabled:cursor-not-allowed"
                      onClick={() => handleCheckout("starter")}
                      disabled={loadingCta === `starter:${interval}`}
                    >
                      {loadingCta === `starter:${interval}`
                        ? "Redirecting..."
                        : interval === "monthly"
                          ? "Start Free Trial"
                          : "Get Started"}
                    </Button>
                    <div className="mt-4">
                      {starterFeatures.map((value, idx) =>
                        renderFeatureCell(
                          value,
                          idx === 0,
                          `starter-${idx}`,
                          idx === AI_PLATFORM_FEATURE_INDEX &&
                            shouldExpandAiPlatformsRow,
                        ),
                      )}
                    </div>
                  </div>

                  <div className="min-h-[668px] h-auto min-w-0">
                    <div className="relative h-full rounded-xl border-2 border-[#FF5E00] px-6 pt-[22px] pb-2">
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full border-2 border-[#FF5E00] bg-white px-2 py-0.5 text-[10px] text-[#FF5E00]">
                        {growthLabel}
                      </div>
                      <h2 className="text-xl font-semibold leading-7">
                        {planMap.growth?.name ||
                          FALLBACK_PLAN_CONTENT.growth.name}
                      </h2>
                      <p className="mt-2 h-[40px] overflow-hidden text-sm text-[#737373]">
                        {planMap.growth?.description ||
                          FALLBACK_PLAN_CONTENT.growth.description}
                      </p>
                      <div className="mt-4">{renderPrice("growth")}</div>
                      <Button
                        className="mt-4 h-9 w-full cursor-pointer bg-[#FF5E00] text-white hover:bg-[#ff7a30] disabled:cursor-not-allowed"
                        onClick={() => handleCheckout("growth")}
                        disabled={loadingCta === `growth:${interval}`}
                      >
                        {loadingCta === `growth:${interval}`
                          ? "Redirecting..."
                          : "Get Started"}
                      </Button>
                      <div className="mt-4">
                        {growthFeatures.map((value, idx) =>
                          renderFeatureCell(
                            value,
                            idx === 0,
                            `growth-${idx}`,
                            idx === AI_PLATFORM_FEATURE_INDEX &&
                              shouldExpandAiPlatformsRow,
                          ),
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="min-h-[668px] h-auto min-w-0 px-6 pt-6 pb-2">
                    <h2 className="text-xl font-semibold leading-7">
                      {planMap.enterprise?.name ||
                        FALLBACK_PLAN_CONTENT.enterprise.name}
                    </h2>
                    <p className="mt-2 h-[40px] overflow-hidden text-sm text-[#737373]">
                      {planMap.enterprise?.description ||
                        FALLBACK_PLAN_CONTENT.enterprise.description}
                    </p>
                    <div className="mt-4 flex h-[52px] items-end">
                      <p className="text-4xl font-semibold leading-none">
                        <LocalizedText id="legacy.custom.1956hjq" />
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      className="mt-4 h-9 w-full cursor-pointer border-[#D4D4D4] bg-white text-[#171717] hover:bg-[#F5F5F5]"
                      onClick={() =>
                        window.open("https://nobori.ai/demo", "_blank")
                      }
                    >
                      <LocalizedText id="legacy.talk.to.sales.1bmbtz8" />
                    </Button>
                    <div className="mt-4">
                      {enterpriseFeatures.map((value, idx) =>
                        renderFeatureCell(
                          value,
                          idx === 0,
                          `enterprise-${idx}`,
                          idx === AI_PLATFORM_FEATURE_INDEX &&
                            shouldExpandAiPlatformsRow,
                        ),
                      )}
                    </div>
                  </div>
                </div>
              )}
            </section>
          </div>
        </div>
      )}
    </>
  );
};

export default OnboardingPlanPage;
